import { mix, shade } from "@/color/oklch";
import { PALETTE_KEYS } from "@/model/palette";
import { TOKENS } from "@/model/tokens";
import { type ExportContext, type Exporter, GENERATED_BY } from "./types";

function base16Map(ctx: ExportContext) {
  const p = (k: (typeof PALETTE_KEYS)[number]) => ctx.color(`palette.${k}`);
  return {
    base00: ctx.color("terminal.background"),
    base01: p("bg2"),
    base02: ctx.color("ui.selection"),
    base03: ctx.color("syntax.comment"),
    base04: p("fg2"),
    base05: ctx.color("terminal.foreground"),
    base06: p("fg0"),
    base07: mix(p("fg0"), ctx.theme.variant === "dark" ? "#ffffff" : "#000000", 0.25),
    base08: p("red"),
    base09: p("orange"),
    base0A: p("yellow"),
    base0B: p("green"),
    base0C: p("cyan"),
    base0D: p("blue"),
    base0E: p("purple"),
    base0F: p("pink"),
  };
}

function yaml(ctx: ExportContext, system: "base16" | "base24", palette: Record<string, string>) {
  return `# ${ctx.appName}, ${GENERATED_BY}
system: "${system}"
name: "${ctx.theme.name.replace(/"/g, "'")}"
author: "themes.utimura.io"
variant: "${ctx.theme.variant}"
palette:
${Object.entries(palette)
  .map(([k, v]) => `  ${k}: "${v}"`)
  .join("\n")}
`;
}

const tintedSteps = (system: string, file: string) => [
  `Save the scheme below as \`${file}\`.`,
  `Use it with any ${system} (tinted-theming) builder or app that accepts a ${system} scheme file.`,
];

export const base16: Exporter = {
  id: "base16",
  label: "Base16",
  app: false,
  description: "tinted-theming base16 scheme (YAML).",
  run: (ctx) => {
    const file = `${ctx.appName}.yaml`;
    return {
      files: [
        {
          path: file,
          filename: file,
          language: "yaml",
          content: yaml(ctx, "base16", base16Map(ctx)),
        },
      ],
      steps: tintedSteps("base16", file),
      verify: [],
      notes: ["base16 has 8 accents: bright ANSI colors and app-specific tokens are not included."],
    };
  },
};

export const base24: Exporter = {
  id: "base24",
  label: "Base24",
  app: false,
  description: "tinted-theming base24 scheme (YAML) with bright colors.",
  run: (ctx) => {
    const c = (id: Parameters<typeof ctx.color>[0]) => ctx.color(id);
    const palette = {
      ...base16Map(ctx),
      base10: c("palette.bg0"),
      base11: shade(c("palette.bg0"), ctx.theme.variant === "dark" ? -0.03 : 0.03),
      base12: c("terminal.ansi9"),
      base13: c("terminal.ansi11"),
      base14: c("terminal.ansi10"),
      base15: c("terminal.ansi14"),
      base16: c("terminal.ansi12"),
      base17: c("terminal.ansi13"),
    };
    const file = `${ctx.appName}.base24.yaml`;
    return {
      files: [
        { path: file, filename: file, language: "yaml", content: yaml(ctx, "base24", palette) },
      ],
      steps: tintedSteps("base24", file),
      verify: [],
      notes: [],
    };
  },
};

export const css: Exporter = {
  id: "css",
  label: "CSS variables",
  app: false,
  description: "Every palette swatch and token as CSS custom properties.",
  run: (ctx) => {
    const name = (id: string) =>
      `--theme-${id
        .replace(/^weechat\.(weechat|irc|buflist)[.:]?/, "weechat-$1-")
        .replace(/[.:]/g, "-")
        .replace(/_/g, "-")
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .toLowerCase()}`;
    const ids = [...PALETTE_KEYS.map((k) => `palette.${k}` as const), ...TOKENS.map((t) => t.id)];
    const body = ids.map((id) => `  ${name(id)}: ${ctx.color(id)};`).join("\n");
    const file = `${ctx.appName}.css`;
    return {
      files: [
        {
          path: file,
          filename: file,
          language: "css",
          content: `/* ${ctx.appName}: "${ctx.theme.name}", ${GENERATED_BY} */\n:root {\n  color-scheme: ${ctx.theme.variant};\n${body}\n}\n`,
        },
      ],
      steps: [`Include \`${file}\` and use the \`--theme-*\` variables.`],
      verify: [],
      notes: [],
    };
  },
};

export const themeJson: Exporter = {
  id: "theme-json",
  label: "Theme JSON",
  app: false,
  description: "The editable theme (palette, references, overrides). Re-import it any time.",
  run: (ctx) => {
    const file = `${ctx.appName}.theme.json`;
    return {
      files: [
        {
          path: file,
          filename: file,
          language: "json",
          content: `${JSON.stringify(ctx.theme, null, 2)}\n`,
        },
      ],
      steps: ["Open https://themes.utimura.io and drop this file on the page to keep editing."],
      verify: [],
      notes: [],
    };
  },
};
